import {
  autocivVehicleTypeMap,
  BonusMalus,
  CitizenStatus,
  citizenStatusMapper,
  detectAgeGroupName,
  determineDriverAge,
  DPeriod,
  dperiodMapper,
  DPersonStatus,
  DPrivelege,
  DSphereUse,
  ExpLimit,
  get00707,
  get01118,
  get01119,
  getByValue,
  getInsuranceSumDNV,
  getMarkByVehicleType,
  getMD,
  getPersonDocumentType,
  getSL,
  getSplitPassportData,
  getTariffProgram,
  getVariants,
  periodMapper,
  transliterateAddressFromCyrillic,
  VehicleType,
  vehicleTypeMapperPrintout,
  vehicleTypeMapperPrintoutOSCPV,
  vehicleTypeMapperToNumber,
  zoneMapper,
} from "@/src/shared/utils/formDataMiddleware";
import {
  formatDate,
  dateDiff,
  parseToKyiv,
  getYear,
  nowInKyiv
} from "@/src/shared/utils/dateTime";
import {
  FOR_TOURISTS_CONST,
  ProposalTableColumns,
  ProposalTableColumnsKeys
} from "@/src/components/ProposalTable/lib/const";
import { useFormContext } from "react-hook-form";
import { TouristFieldType } from "@/src/shared/schemas";
import { has, isArray, isEmpty, isNil, isObject, isString, sum, toNumber } from "lodash";
import { getColContextValueString } from "@/src/components/ProposalTable/lib/utils";
import { useContextStore } from "@/src/stores/useContextStore";
import { safeParseJson } from "@/src/shared/utils/form";
import { getAgeFromDate, normalizedDateFormat } from "@/src/shared/utils/parseDatePickerValue";
import { getAgeRangeKey } from "@/src/components/InsuredTourist/lib/utils";
import { transliterate } from "@/src/components/Fields/AutoNumberField/lib/transliterate.helper";
import { useProcessInstanceStore } from "../../stores/useProcessInstanceStore";

const createStreetLine = ({
  streetType,
  street,
  houseNumber,
  flatNumber,
}: {
  streetType: string | null;
  street: string | null;
  houseNumber: string | null;
  flatNumber: string | null;
}) => {
  let line = `${streetType?.toLowerCase() || ""} ${street || ""}`;
  if (houseNumber) {
    line += ", буд " + houseNumber;
  }
  if (flatNumber) {
    line += ", кв " + flatNumber;
  }

  return line.trim();
};

export const getObjectSignCodeProduct = (formData: Record<string, any>) => {
  if (formData.pickedCrosses?.length > 0) {
    return formData.objectSignCode_Product;
  }
  return formData.codeToVerify;
};

const generalDateFields = ["startDate", "endDate", "birthDate"];
const normalizeDate = (date: string) => {
  return formatDate(date, normalizedDateFormat);
}

const formatFieldsDate = (formData: Record<string, any>, fields: string[]) => {
  const res: Record<string, any> = {}

  fields.forEach((field) => {
    if (formData[field]) {
      res[field] = normalizeDate(formData[field]);
    }
  }
  );
  return res;
}

export const getPlanNameReqData = (
  planName: string,
  formDate: Record<string, any>,
): Record<string, any> | null => {
  const staticValueKey = getColContextValueString(planName);
  const columnStaticValues: Record<string, any> = formDate[staticValueKey];
  if (!isObject(columnStaticValues)) {
    return null;
  }

  const daysAbroad =
    formDate["travelType"]?.value === "one-time"
      ? toNumber(formDate["daysAbroad-oneTimeTrip"])
      : toNumber(formDate["daysAbroad-multiTimeTrip"]?.value);
  const civilSuminsured =
    daysAbroad <= 30
      ? { label: "10 000", value: "10000.00", type: "select-number" }
      : columnStaticValues.civilSuminsured;
  const civilCurrency =
    daysAbroad <= 30
      ? columnStaticValues.helpAssistance_Currency
      : columnStaticValues.civilCurrency;
  const helpAssistance_Suminsured =
    daysAbroad < 120 && planName === "Vip"
      ? { label: "100 000", value: "100000.00", type: "select-number" }
      : columnStaticValues.helpAssistance_Suminsured;
  return {
    ...columnStaticValues,
    civilSuminsured,
    helpAssistance_Suminsured,
    civilCurrency,
    FinancialRisk_Currency: civilCurrency?.value, // financialRisk_Currency = civilCurrency = helpAssistance_Currency
    financialRisk_Currency: civilCurrency?.value // financialRisk_Currency = civilCurrency = helpAssistance_Currency
  };
};

const getCategoryReqData = (formData: Record<string, any>) => {
  return ProposalTableColumns.reduce((acc, column) => {
    const staticValueKey = getColContextValueString(column);
    const planNameReqData = getPlanNameReqData(column, formData);
    if (!planNameReqData) return {};
    return {
      ...acc,
      [staticValueKey]: planNameReqData,
    };
  }, {});
};

export const recalcInsuredCounts = (touristsArray: TouristFieldType[]) => {
  const ageObj = {
    insuredCountAgeFrom0To14: 0,
    insuredCountAgeFrom15To24: 0,
    insuredCountAgeFrom25To64: 0,
    insuredCountAgeFrom65To69: 0,
    insuredCountAgeFrom70To74: 0,
  };

  if (!touristsArray || !touristsArray.length) return ageObj;

  touristsArray.forEach(({ tourist_birthDate }: any) => {
    if (isString(tourist_birthDate) && !isEmpty(tourist_birthDate)) {
      const age = getAgeFromDate(tourist_birthDate);
      if (!isNil(age)) {
        const ageRangeKey = getAgeRangeKey(age);

        if (ageRangeKey && has(ageObj, ageRangeKey)) {
          ageObj[ageRangeKey] += 1;
        } else {
          console.log(`Age ${age} does not fall into any specified range`);
        }
      }
    }
  });

  return ageObj;
};

export const useFormDataMiddleware = () => {
  const { getValues } = useFormContext();
  const getContextVariable = useContextStore((state) => state.getVariable);

  const cleanTouristListFromShadowItems = (tourists: TouristFieldType[]) => {
    if (!isArray(tourists)) return;
    return tourists.filter((tourist) => {
      return (
        !isNil(tourist.tourist_firstName) && !isNil(tourist.tourist_lastName)
      );
    }).map((tourist) => {
      if (tourist.isInsurerTourist) {
        const [
          tourist_lastName,
          tourist_firstName,
          tourist_lastNameLatin,
          tourist_firstNameLatin,
          tourist_citizenship,
          tourist_foreignPassportNumber,
          tourist_identCode,
          tourist_birthDate,
          tourist_street,
          tourist_houseNumber,
          tourist_flatNumber,
          tourist_city,
          tourist_streetType,
        ] = getValues([
          "lastName",
          "firstName",
          "lastNameLatin",
          "firstNameLatin",
          "citizenship",
          "foreignPassportNumber",
          "identCode",
          "birthDate",
          "street",
          "houseNumber",
          "flatNumber",
          "city",
          "streetType",
        ])
        return {
          ...tourist,
          tourist_lastName,
          tourist_firstName,
          tourist_lastNameLatin,
          tourist_firstNameLatin,
          tourist_citizenship,
          tourist_foreignPassportNumber,
          tourist_identCode,
          tourist_birthDate: normalizeDate(tourist_birthDate),
          tourist_street,
          tourist_houseNumber,
          tourist_flatNumber,
          tourist_city,
          tourist_streetType,
        }
      }
      return tourist
    })
  };

  const formDataMiddleware = (actionName: string | null, formData: any) => {
    const streetLine = createStreetLine({
      streetType: formData.streetType?.label,
      street: formData.street,
      houseNumber: formData.houseNumber,
      flatNumber: formData.flatNumber,
    });

    const greenCardReservePolicyFields = {
      regNo: formData.plateNumber,
      periodId: periodMapper(formData.insurancePeriod?.value),
      personStatusId: 1, // 1 – for phisical person
      citizenStatusId: citizenStatusMapper(formData.citizenship?.value),
      vehicleTypeId: vehicleTypeMapperToNumber(
        formData.vehicleCategory?.value,
      ),
      autoDescr: `${formData.brand?.value || ""} ${formData.model?.value || ""}`,
      zoneId: zoneMapper(formData.territoryOfCoverage?.value),
    };

    const dperiod = getByValue(
      DPeriod,
      dperiodMapper(formData.datePeriod?.value) ?? DPeriod.DPeriod_13,
    );
    const autocivilkaReservePolicyFields = {
      autoDescr: `${formData.brand?.value || ""} ${formData.model?.value || ""}`,

      constructionYear: getYear(formData.constructionYear)?.toString(),
      dbonusMalusID: getByValue(BonusMalus, formData.bonusmalus), // mapper
      dcitizenStatusID: getByValue(
        CitizenStatus,
        citizenStatusMapper(formData.citizenship?.value),
      ), // new citizenStatusMapper(formData.citizenship?.value),
      // "dcityID": formData.useCity?.value || "", // formValue["useCity"].value
      dpersonDocTypeId: getPersonDocumentType(formData.documentType?.value),
      dcityID: 0, // hardcoded
      dexpLimitID: getByValue(ExpLimit, ExpLimit.DExpLimit_1), // defaultValue = "DExpLimit_1" || 1 рік
      dmarkID:
        formData.markId || getMarkByVehicleType(formData.vehicleCategory?.value), // mark з serachmtsbumvs повернутися має || getMarkIdByType
      dmodelID: formData.modelId || 0, // BE додаю поле "modelId" – mtsbuSearchMvs.vehicleModel.mtsbuId || 0
      dperiodID: dperiod, // defaultValue = "DPeriod_13" || 12 місяців
      dpersonStatusID: getByValue(
        DPersonStatus,
        DPersonStatus.DPersonStatus_1,
      ), // defaultValue = "DPersonStatus_1" || завжди фізична особа
      dprivelegeID: getByValue(DPrivelege, DPrivelege.DPrivelege_0), // defaultValue = "DPrivelege_0" || завжди без пільг
      dsphereUseID: getByValue(
        DSphereUse,
        formData.taxiUsage === "true" || formData.taxiUsage === true
          ? DSphereUse.DSphereUse_2
          : DSphereUse.DSphereUse_1,
      ), // getTaxiUsage === "true" ? "DSphereUse_2" : "DSphereUse_1"
      dvehicleTypeID:
        autocivVehicleTypeMap[formData.vehicleType?.value as VehicleType], // vehicleTypeMapper(formData.vehicleCategory?.value),
      vehicleUsage:
        dperiod === getByValue(DPeriod, DPeriod.DPeriod_13)
          ? "111111111111"
          : null,
      usageMonths:
        dperiod === getByValue(DPeriod, DPeriod.DPeriod_13)
          ? "111111111111"
          : null,
    };

    const getIsPVINeeded = () => {
      const mileage = getValues("mileage");
      const newCar = getValues("newCar");
      const constructionYear = getValues("constructionYear");

      const currentYear = nowInKyiv().year;
      const carYear = getYear(constructionYear) || 0;
      const yearsDifference = currentYear - carYear;

      if (!newCar || Number(mileage) > 1000 || yearsDifference > 3) {
        return { needPVI: "true" };
      }

      return { needPVI: "false" };
    };

    const getLegkyiZahystPremiumPickedBt = () => {
      const pickedLegkyiZahyst = formData.pickedCrosses?.find((cross: string) =>
        cross.includes("LegkyiZahystPremium"),
      );
      if (!pickedLegkyiZahyst) return;
      const btDCV = formData[`${pickedLegkyiZahyst}_btDCV`];
      const btDNV = formData[`${pickedLegkyiZahyst}_btDNV`];
      return { btDCV, btDNV };
    };

    const getPersons = () => {
      const pickedDMSDTP = formData.pickedCrosses?.find((cross: string) =>
        cross.includes("DMSDTP"),
      );
      if (pickedDMSDTP) {
        return [
          {
            dmsPerson_surname: formData.lastName,
            dmsPerson_givenName: formData.firstName,
            dmsPerson_middleName: formData.middleName,
            dmsPerson_birthDate: formatDate(formData.birthDate, "dd.MM.yyyy"),
            dmsPerson_registrationAddress_unstructured: `${formData.city?.label || ""}, ${streetLine}`,
            dmsPerson_ipn: formData.identCode,
            mainDmsDocument_serie: formData.passportSeries,
            mainDmsDocument_number: formData.passportNumber,
            mainDmsDocument_issueDate: formatDate(formData.issueDate, "dd.MM.yyyy"),
            mainDmsDocument_issueOrganization: formData.issuingAuthority,
          },
        ];
      }
      const pickedTravelPackage = formData.pickedTravelCalculationCategory;
      if (pickedTravelPackage) {
        return formData.forTourists.map((tourist: any) => {
          const age = getAgeFromDate(tourist.tourist_birthDate);

          const calculationCategory = formData[
            "pickedTravelCalculationCategory"
          ] as string;
          const ageGroupName = detectAgeGroupName(age || 0);

          const premiumHelpAssistance =
            formData[
            `${calculationCategory}_${ageGroupName}_Premium_HELP(ASSISTANCE)`
            ];
          const premiumFinancialRisk =
            formData[
            `${calculationCategory}_${ageGroupName}_Premium_FinancialRisk`
            ];
          const premiumAccident =
            formData[`${calculationCategory}_${ageGroupName}_Premium_Accident`];
          const premiumCivil =
            formData[`${calculationCategory}_${ageGroupName}_Premium_Civil`];
          const tariffAccident =
            formData[`${calculationCategory}_${ageGroupName}_Tariff_Accident`];
          const totalPremium = premiumHelpAssistance + premiumFinancialRisk + premiumAccident + premiumCivil;

          const { documentSerie, documentNumber } = getSplitPassportData(
            tourist.tourist_foreignPassportNumber,
          );

          const streetLine = createStreetLine({
            streetType: tourist.tourist_streetType?.label,
            street: tourist.tourist_street,
            houseNumber: tourist.tourist_houseNumber,
            flatNumber: tourist.tourist_flatNumber,
          });

          const address = `Україна, ${tourist.tourist_city?.label || ""}, ${streetLine}`;

          return {
            travelPerson_translitSurname: tourist.tourist_lastNameLatin,
            travelPerson_translitGivenName: tourist.tourist_firstNameLatin,
            riskOfTerrorism: formData.terrorism === true ? "true" : "false",
            mainTravelDocument_serie: documentSerie ?? "",
            mainTravelDocument_number: documentNumber ?? "",
            travelPerson_ipn: tourist.tourist_identCode,
            travelPerson_birthDate: formatDate(tourist.tourist_birthDate, "dd.MM.yyyy"),
            travelPerson_registrationAddress_translitUnstructured:
              transliterateAddressFromCyrillic(address.trim(), true),
            complexGuarantees9_4_405_commercialTariff: tariffAccident?.toString(),
            complexGuarantees10_3_305_commercialPremium_number:
              premiumHelpAssistance,
            complexGuarantees9_4_405_commercialPremium_number: premiumAccident,
            complexGuarantees7_44_4403_commercialPremium_number:
              premiumFinancialRisk,
            complexGuarantees10_3_313_commercialPremium_number: premiumCivil,
            totalCommercialPremium_number: totalPremium,
            sport: ["Professional sport", "Extreme sport"].includes(
              formData.travelPurpose?.value,
            )
              ? formData.travelPurpose?.value
              : "",
            manualWork:
              formData.travelPurpose?.value?.toLowerCase() === "work"
                ? "true"
                : "false",
          };
        });
      }
    };

    const getTouristCount = (): number => {
      return formData.forTourists?.length || 0;
    };

    const getFinanceRiskSum = (pickedTravelPackage: string) => {
      const sum = {
        [ProposalTableColumnsKeys.Standard]: "40.00",
        [ProposalTableColumnsKeys.Business]: "3000.00",
        [ProposalTableColumnsKeys.Elit]: "5000.00",
        [ProposalTableColumnsKeys.Vip]: "10000.00",
      }
      return sum[pickedTravelPackage as ProposalTableColumnsKeys] || "0.00";
    }

    const getPickedTravelPackage = (): Record<string, string | number> => {
      const pickedTravelPackage = formData.pickedTravelCalculationCategory;
      console.log("pickedTravelPackage", pickedTravelPackage);
      if (!pickedTravelPackage) return {};
      return {
        tariffProgram: pickedTravelPackage.toUpperCase(),
        pickedTravelPackage_Premium_Accident:
          formData[`${pickedTravelPackage}_Premium_Accident`],
        pickedTravelPackage_Premium_Civil:
          formData[`${pickedTravelPackage}_Premium_Civil`],
        pickedTravelPackage_Premium_FinancialRisk:
          formData[`${pickedTravelPackage}_Premium_FinancialRisk`],
        pickedTravelPackage_Premium_HelpAssistance:
          formData[`${pickedTravelPackage}_Premium_HELP(ASSISTANCE)`],
        pickedTravelPackage_Tariff_Accident:
          formData[`${pickedTravelPackage}_Tariff_Accident`],
        pickedTravelPackage_SumInsured_HelpAssistance:
          formData[`helpAssistance_Suminsured`]?.value,
        pickedTravelPackage_SumInsured_Civil:
          formData[`civilSuminsured`]?.value,
        pickedTravelPackage_SumInsured_Accident:
          formData[`suminsured_Accident`]?.value,
        pickedTravelPackage_SumInsured_FinancialRisk: getFinanceRiskSum(pickedTravelPackage),
        totalCommercialPremium: formData[`${pickedTravelPackage}_finalPremium`],
        insuredCount: sum(Object.values(formData.insuredTourists)),
      };
    };

    const getTouristPassportData = () => {
      const foreignPassportNumber = formData.foreignPassportNumber;
      if (!foreignPassportNumber) return {};
      const { documentSerie, documentNumber } = getSplitPassportData(
        foreignPassportNumber,
      );
      return {
        passportNumber: documentNumber,
        passportSeries: documentSerie,
      };
    };

    const { btDCV: pickedLegkyiZahyst_btDCV, btDNV: pickedLegkyiZahyst_btDNV } =
      getLegkyiZahystPremiumPickedBt() || {};

    const getReserMtsbuDate = () => {
      const reserveDate = formData.reserveDate;
      if (!reserveDate) return {};

      const reserveDateObj = parseToKyiv(reserveDate);
      if (!reserveDateObj) return {};

      return {
        mtsbuOffer_creationDate: formatDate(reserveDate, "dd.MM.yyyy HH:mm:ss"),
        mtplContract_creationDateDay: formatDate(reserveDate, "dd"),
        mtplContract_creationDateMonth: formatDate(reserveDate, "MM"),
        mtplContract_creationDateYear: formatDate(reserveDate, "yyyy"),
        mtplContract_creationDateTime: formatDate(reserveDate, "HH:mm:ss"),
      };
    };

    const getPriceWithCrosses = (formData: Record<string, any>) => {
      const currentPremium = formData.premium;
      const pickedCrosses = formData.pickedCrosses;

      if (pickedCrosses?.length > 0) {
        const crossesPrice = pickedCrosses.reduce(
          (sum: number, curr: string) => {
            const pickedCross = useContextStore.getState().getVariable(curr)
              ?.value as any;

            if (!pickedCross) {
              return sum;
            }

            const pickedCrossPrice = safeParseJson(pickedCross)?.value;
            return sum + Number(pickedCrossPrice || 0);
          },
          0,
        );
        const priceWithCrosses =
          (formData.DMSDTPPremium_picked_premium || 0) +
          (formData.LegkyiZahystPremium_picked_premium || 0);
        return currentPremium + crossesPrice;
      }
      return currentPremium;
    };

    const getFinalPremiumWithoutCross = (
      formData: Record<string, any>,
    ): number => {
      const currentPremium = formData.finalPremium;
      const pickedCrosses = formData.pickedCrosses;
      if (pickedCrosses?.length > 0) {
        const priceWithCrosses =
          (formData.DMSDTPPremium_picked_premium || 0) +
          (formData.LegkyiZahystPremium_picked_premium || 0);
        return currentPremium - priceWithCrosses;
      }
      return currentPremium;
    };


    const getCrossData = (formData: Record<string, any>) => {
      const isDateChanged = JSON.stringify(formData.datePeriod) !== getContextVariable("datePeriod")?.value;
      const isVehicleCategoryChanged = JSON.stringify(formData.vehicleCategory) !== getContextVariable("vehicleCategory")?.value;
      const isCalculationStep = useProcessInstanceStore.getState().processInstance.activeStep.orderStatus === "CALCULATION"
      console.log("isCalculationStep", isCalculationStep, useProcessInstanceStore.getState())
      const isRecalculation = isCalculationStep && (actionName === "AutocivilkaTariffCalculator") && (isDateChanged || isVehicleCategoryChanged);

      if (isRecalculation) {
        return {
          pickedCrosses: [],
          finalPremiumWithoutCross: getFinalPremiumWithoutCross(formData),
          priceWithCrosses: formData.premium,
          isCrossesPicked: false,
          DMSDTPTariff_picked: "null",
          DMSDTPPremium_picked_premium: 0,
          LegkyiZahystPremium_picked_premium: 0,
        }
      }

      return {
        pickedCrosses: formData.pickedCrosses ?? [],
        finalPremiumWithoutCross: getFinalPremiumWithoutCross(formData),
        priceWithCrosses: getPriceWithCrosses(formData),
        isCrossesPicked: formData.pickedCrosses?.length > 0,
        DMSDTPTariff_picked: formData.DMSDTPTariff_picked ?? "null",
        DMSDTPPremium_picked_premium: formData.DMSDTPPremium_picked_premium ?? 0,
        LegkyiZahystPremium_picked_premium: formData.LegkyiZahystPremium_picked_premium ?? 0,
      }

    }

    return {
      ...formData,
      ...formatFieldsDate(formData, generalDateFields),
      primaryInsuredParty_registrationAddress_Unstructured: `Україна, ${formData.city?.label || ""}, ${streetLine}`,
      primaryInsuredParty_registrationAddress_translitUnstructured:
        transliterateAddressFromCyrillic(
          `Україна, ${formData.city?.label || ""}, ${streetLine}`,
          true,
        ),
      primaryInsuredParty_registrationAddress_streetLine: streetLine,
      vehicle_beneficiary_registrationAddress_streetLine: streetLine,
      vehicle_registrationBrandModel:
        `${formData.brand?.label || ""} ${formData.model?.label || ""}`.trim(),
      vehicleTypePrintout: vehicleTypeMapperPrintout(
        formData.vehicleCategory?.value,
      ),
      vehicleTypePrintoutOSCPV: vehicleTypeMapperPrintoutOSCPV(
        formData.vehicleType?.value,
      ),
      vehicleTypeValue: formData.vehicleType?.value,
      durationValue: formData.duration?.value,
      datePeriodValue: formData.datePeriod?.value,
      territoryOfCoverageValue: formData.territoryOfCoverage?.value,
      LegkyiZahystPremium_picked_btDCV: pickedLegkyiZahyst_btDCV,
      LegkyiZahystPremium_picked_btDNV: pickedLegkyiZahyst_btDNV,
      LegkyiZahystPremium_picked_InsuranceSumDNV: getInsuranceSumDNV(
        formData.LegkyiZahystPremium_picked_insuranceSum,
      ),
      persons: getPersons(),
      touristCount: getTouristCount(),
      countryTravelZoneValue: formData.countryTravelZone?.value,
      contractDurationInDays: (dateDiff(
        formData.startDate,
        formData.endDate,
        "days"
      ) || 0) + 1,
      translitGivenName: `${formData.firstNameLatin} ${formData.middleNameLatin}`,
      ...getPickedTravelPackage(),
      ...getTouristPassportData(),
      work:
        formData.travelPurpose?.value?.toLowerCase() === "work"
          ? "true"
          : "false",
      sport: ["Professional sport", "Extreme sport"].includes(
        formData.travelPurpose?.value,
      )
        ? formData.travelPurpose?.value
        : "",
      freeFieldsvariants: getVariants(
        formData.useCity?.label?.split(" (")[0],
        formData.LegkyiZahystPremium_picked_insuranceSum,
      ),
      vehicleRegisterCityName: formData.useCity?.label?.split(" (")[0], // oscpv
      mainPerson_complexGuarantees9_7_704_basicGuarantees00706_guaranteeLimit:
        getMD(formData.DMSDTPPremium_picked_insuranceSum),
      mainPerson_complexGuarantees9_7_704_basicGuarantees01164_guaranteeLimit:
        getSL(formData.DMSDTPPremium_picked_insuranceSum),
      mainPerson_complexGuarantees9_7_704_basicGuarantees00707_guaranteeLimit:
        get00707(formData.DMSDTPPremium_picked_insuranceSum),
      mainPerson_complexGuarantees9_7_704_basicGuarantees01118_guaranteeLimit:
        get01118(formData.DMSDTPPremium_picked_insuranceSum),
      mainPerson_complexGuarantees9_7_704_basicGuarantees01119_guaranteeLimit:
        get01119(formData.DMSDTPPremium_picked_insuranceSum),
      tariffProgramDMSDTP: getTariffProgram(
        formData.DMSDTPPremium_picked_insuranceSum,
      ),
      [FOR_TOURISTS_CONST]: cleanTouristListFromShadowItems(
        getValues(FOR_TOURISTS_CONST),
      ),
      ...getReserMtsbuDate(),
      objectSignCode_Product: getObjectSignCodeProduct(formData),
      ...getCategoryReqData(formData),
      ...getValues("insuredTourists"),
      ...greenCardReservePolicyFields,
      ...autocivilkaReservePolicyFields,
      // address – reserve policy field
      address: formData.flowCode === "GreenCard_01.01.2025" ? transliterateAddressFromCyrillic(
        `${formData.city?.label || ""}, ${streetLine}`,
        true,
      ) : `${formData.useCity?.label || ""}, ${streetLine}`,
      ...getIsPVINeeded(),
      ...getCrossData(formData),
      // tariffication oscpv
      driverAge: determineDriverAge(formData.minDriverAge, formData.maxDriverAge),
      plateNumber: formData.plateNumber ? transliterate(formData.plateNumber, "numberPlate") : null,
    };
  };

  return {
    formDataMiddleware,
    cleanTouristListFromShadowItems,
  };
};
